import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, Clock, MapPin, BookOpen, Users, ChevronLeft, ChevronRight, RefreshCw, FileText, Search, Printer, Download, ChevronDown, CheckCircle, XCircle, AlertCircle, ClipboardList, Save } from 'lucide-react';
import { getStudentsForSchedule, recordAttendanceBatch } from '@/hooks/controlEscolarService';
import type { StudentForAttendance } from '@/hooks/controlEscolarTypes';
import { useAuth } from '@/hooks/useAuth';

type AttendanceStatus = 'presente' | 'ausente' | 'justificado' | 'tardanza';

interface CalendarEvent {
  id: number;
  title: string;
  start?: string;
  end?: string;
  daysOfWeek?: number[];
  startTime?: string;
  endTime?: string;
  startRecur?: string;
  endRecur?: string;
  extendedProps: {
    classroom: string;
    classroomId?: number;
    courseId?: number;
    courseName?: string;
    courseCode?: string;
    program: string;
    programCode?: string;
    programId?: number;
    period: string;
    periodId?: number;
    periodStartDate?: string;
    periodEndDate?: string;
    startDate?: string;
    endDate?: string;
    day?: string;
    startTime?: string;
    endTime?: string;
    sessionNumber?: number;
    totalSessions?: number;
  };
}

interface TodayClass {
  id: number;
  time: string;
  course: string;
  classroom: string;
  program: string;
  period: string;
  sessionNumber?: number;
  totalSessions?: number;
}

interface TeacherInfo {
  id: number;
  name: string;
}

interface ProfessorOption {
  id: number;
  name: string;
}

interface WeekSummary {
  weekStart: string;
  weekEnd: string;
  subjectCount: number;
  classCount: number;
  subjects: Array<{
    courseName: string;
    courseCode: string;
    groups: string[];
    sessions: string[];
  }>;
  rows: Array<{
    id: number;
    date: string;
    day: string;
    startTime: string;
    endTime: string;
    courseCode: string;
    courseName: string;
    classroom: string;
    program: string;
    programCode: string;
    period: string;
    sessionLabel: string | null;
    sessionNumber: number | null;
    totalSessions: number;
  }>;
}

const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const dayNamesShort = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

export default function AgendaDocente() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Justificado/Tardanza solo los fija Control Escolar (attendance:justify); el maestro marca presente o ausente.
  const canJustify = user?.role === 'Admin' || (user?.permissions?.includes('attendance:justify') ?? false);
  const canViewGradebook = user?.permissions?.includes('gradebook:view') ?? true;
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [pendingEvents, setPendingEvents] = useState<CalendarEvent[]>([]);
  const [todayClasses, setTodayClasses] = useState<TodayClass[]>([]);
  const [teacher, setTeacher] = useState<TeacherInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'day' | 'week' | 'report'>('day');

  const [professors, setProfessors] = useState<ProfessorOption[]>([]);
  const [selectedProfessorId, setSelectedProfessorId] = useState<number | null>(null);
  const [myTeacherId, setMyTeacherId] = useState<number | null>(null);
  const [professorsLoaded, setProfessorsLoaded] = useState(false);
  const [canViewAll, setCanViewAll] = useState(false);

  const [weekSummaries, setWeekSummaries] = useState<WeekSummary[]>([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportStartDate, setReportStartDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState('');

  // Attendance panel state
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [selectedEventDate, setSelectedEventDate] = useState<string>('');
  const [attendanceStudents, setAttendanceStudents] = useState<StudentForAttendance[]>([]);
  const [attendanceMap, setAttendanceMap] = useState<Record<number, { status: AttendanceStatus; comments: string }>>({});
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceSaving, setAttendanceSaving] = useState(false);
  const [attendanceMsg, setAttendanceMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);


  useEffect(() => {
    fetchProfessors();
  }, []);

  useEffect(() => {
    if (professorsLoaded && selectedProfessorId) {
      fetchAgendaForProfessor(selectedProfessorId);
    }
  }, [selectedProfessorId, professorsLoaded]);



  const fetchProfessors = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/teacher-agenda/professors', { credentials: 'include' });

      if (!response.ok) {
        setError('Error al cargar la lista de profesores');
        setLoading(false);
        return;
      }

      const data = await response.json();
      setProfessors(data.professors || []);
      setMyTeacherId(data.myTeacherId || null);
      setCanViewAll(!!data.canViewAll);

      if (data.myTeacherId) {
        setSelectedProfessorId(data.myTeacherId);
      } else if (data.professors?.length > 0) {
        setSelectedProfessorId(data.professors[0].id);
      }

      setProfessorsLoaded(true);
    } catch (err: any) {
      console.error('Error fetching professors:', err);
      setError('Error al cargar la lista de profesores');
      setLoading(false);
    }
  };

  const fetchAgendaForProfessor = async (professorId: number) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`/api/teacher-agenda/agenda/${professorId}`, { credentials: 'include' });

      if (!response.ok) {
        if (response.status === 404) {
          setError('Profesor no encontrado');
        } else {
          setError('Error al cargar la agenda');
        }
        setEvents([]);
        setTeacher(null);
        return;
      }

      const data = await response.json();
      setEvents(data.events || []);
      setTeacher(data.teacher || null);

      fetchTodayClassesForProfessor(professorId);
      fetchPendingSessions(professorId);
    } catch (err: any) {
      console.error('Error fetching agenda:', err);
      setError('Error al cargar la agenda');
    } finally {
      setLoading(false);
    }
  };

  // Clases ya pasadas sin asistencia: el grupo va atrasado y la clase "que toca hoy" por calendario
  // no es la que realmente se imparte. El maestro las toma desde aquí con la fecha real de hoy.
  const fetchPendingSessions = async (professorId: number) => {
    try {
      const response = await fetch(`/api/teacher-agenda/pending-sessions/${professorId}`, { credentials: 'include' });
      if (!response.ok) { setPendingEvents([]); return; }
      const data = await response.json();
      setPendingEvents(data.events || []);
    } catch {
      setPendingEvents([]);
    }
  };

  const fetchTodayClassesForProfessor = async (professorId: number) => {
    try {
      const response = await fetch(`/api/teacher-agenda/agenda/${professorId}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        const allEvents: CalendarEvent[] = data.events || [];
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0];
        const dayOfWeek = today.getDay();

        const todayEvts = allEvents.filter(event => {
          if (event.start) {
            return event.start.split('T')[0] === todayStr;
          }
          if (event.daysOfWeek) {
            if (!event.daysOfWeek.includes(dayOfWeek)) return false;
            if (event.startRecur && todayStr < event.startRecur) return false;
            if (event.endRecur && todayStr > event.endRecur) return false;
            return true;
          }
          return false;
        });

        setTodayClasses(todayEvts.map(e => ({
          id: e.id,
          time: `${e.extendedProps.startTime || ''} - ${e.extendedProps.endTime || ''}`,
          course: e.title,
          classroom: e.extendedProps.classroom,
          program: e.extendedProps.program,
          period: e.extendedProps.period,
          sessionNumber: e.extendedProps.sessionNumber,
          totalSessions: e.extendedProps.totalSessions,
        })));
      }
    } catch (err) {
      console.error('Error fetching today classes:', err);
    }
  };

  const fetchWeeklyReport = async () => {
    if (!selectedProfessorId) return;
    try {
      setReportLoading(true);
      const params = new URLSearchParams();
      if (reportStartDate) params.set('startDate', reportStartDate);
      if (reportEndDate) params.set('endDate', reportEndDate);

      const response = await fetch(
        `/api/teacher-agenda/weekly-report/${selectedProfessorId}?${params.toString()}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        setWeekSummaries(data.weekSummaries || []);
      }
    } catch (err) {
      console.error('Error fetching weekly report:', err);
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'report' && selectedProfessorId) {
      fetchWeeklyReport();
    }
  }, [viewMode, selectedProfessorId]);

  const professorName = professors.find(p => p.id === selectedProfessorId)?.name
    || teacher?.name
    || 'Docente';

  const fmtDate = (dateStr: string) =>
    new Date(dateStr + 'T00:00:00').toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });

  const printReport = () => {
    const periodLabel = reportStartDate && reportEndDate
      ? `${fmtDate(reportStartDate)} – ${fmtDate(reportEndDate)}`
      : 'Todas las semanas';
    const printDate = new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });

    const weeksHtml = weekSummaries.map(week => {
      const rowsHtml = week.rows.map(row => `
        <tr>
          <td>${row.date ? new Date(row.date + 'T00:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'short' }) : '-'}</td>
          <td style="text-transform:capitalize">${row.day || '-'}</td>
          <td style="white-space:nowrap">${row.startTime} - ${row.endTime}</td>
          <td><strong>${row.courseCode}</strong> <span style="color:#6b7280;font-size:11px">${row.courseName}</span></td>
          <td style="font-size:11px">${row.program}</td>
          <td>${row.classroom}</td>
          <td>${row.sessionLabel ? `<span class="badge">${row.sessionLabel}</span>` : '-'}</td>
        </tr>`).join('');

      const subjectsHtml = week.subjects.map(s => `
        <div class="subject-chip">
          <strong>${s.courseCode}</strong> ${s.courseName}
          <span style="color:#6b7280"> · ${s.groups.join(', ')}</span>
        </div>`).join('');

      return `
        <div class="week-block">
          <div class="week-header">
            <span>Semana: ${fmtDate(week.weekStart)} – ${fmtDate(week.weekEnd)}</span>
            <span>${week.subjectCount} materia${week.subjectCount !== 1 ? 's' : ''} · ${week.classCount} clase${week.classCount !== 1 ? 's' : ''}</span>
          </div>
          <div class="subjects-row">${subjectsHtml}</div>
          <table>
            <thead>
              <tr>
                <th>Fecha</th><th>Día</th><th>Horario</th><th>Materia</th><th>Grupo</th><th>Aula</th><th>Clase</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </div>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html lang="es"><head><meta charset="UTF-8"/>
<title>Reporte Agenda Docente</title>
<style>
  * { box-sizing:border-box; margin:0; padding:0; }
  body { font-family:Arial,sans-serif; font-size:13px; color:#111; padding:24px; }
  .header { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:2px solid #111; padding-bottom:12px; margin-bottom:16px; }
  .title { font-size:18px; font-weight:bold; }
  .subtitle { font-size:12px; color:#6b7280; margin-top:3px; }
  .meta { text-align:right; font-size:12px; color:#374151; }
  .week-block { margin-bottom:24px; border:1px solid #e5e7eb; border-radius:6px; overflow:hidden; }
  .week-header { background:#1d4ed8; color:#fff; padding:8px 12px; display:flex; justify-content:space-between; font-size:13px; font-weight:600; }
  .subjects-row { display:flex; flex-wrap:wrap; gap:6px; padding:8px 12px; background:#eff6ff; border-bottom:1px solid #dbeafe; }
  .subject-chip { background:#dbeafe; color:#1e40af; border-radius:4px; padding:2px 8px; font-size:11px; }
  table { width:100%; border-collapse:collapse; font-size:12px; }
  thead th { background:#f3f4f6; padding:6px 8px; text-align:left; font-size:11px; text-transform:uppercase; color:#374151; }
  tbody td { padding:5px 8px; border-bottom:1px solid #f3f4f6; }
  .badge { background:#dbeafe; color:#1e40af; border-radius:10px; padding:1px 7px; font-size:11px; font-weight:600; }
  @media print { body { padding:10px; } .week-block { page-break-inside:avoid; } }
</style></head>
<body>
  <div class="header">
    <div>
      <div class="title">Reporte de Agenda Docente</div>
      <div class="subtitle">Profesor: ${professorName} · Período: ${periodLabel}</div>
    </div>
    <div class="meta">Impreso el ${printDate}</div>
  </div>
  ${weeksHtml}
  <script>window.onload = () => { window.print(); }<\/script>
</body></html>`;

    const win = window.open('', '_blank', 'width=900,height=700');
    if (win) { win.document.write(html); win.document.close(); }
    else alert('Permite las ventanas emergentes para imprimir el reporte.');
  };

  const downloadCsv = () => {
    const headers = ['Semana', 'Fecha', 'Día', 'Horario', 'Código', 'Materia', 'Grupo', 'Aula', 'Clase'];
    const rows = weekSummaries.flatMap(week =>
      week.rows.map(row => [
        `${fmtDate(week.weekStart)} - ${fmtDate(week.weekEnd)}`,
        row.date ? new Date(row.date + 'T00:00:00').toLocaleDateString('es-MX') : '',
        row.day || '',
        `${row.startTime} - ${row.endTime}`,
        row.courseCode,
        row.courseName,
        row.program,
        row.classroom,
        row.sessionLabel || '',
      ])
    );
    const csv = [headers, ...rows]
      .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reporte-agenda-${professorName.replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getEventsForDate = (date: Date): CalendarEvent[] => {
    const dateStr = date.toISOString().split('T')[0];
    const dayOfWeek = date.getDay();

    return events.filter(event => {
      if (event.start) {
        const eventDate = event.start.split('T')[0];
        return eventDate === dateStr;
      }

      if (event.daysOfWeek) {
        if (!event.daysOfWeek.includes(dayOfWeek)) return false;
        if (event.startRecur && dateStr < event.startRecur) return false;
        if (event.endRecur && dateStr > event.endRecur) return false;
        return true;
      }

      return false;
    });
  };

  const getWeekDates = (date: Date): Date[] => {
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - day);

    const dates: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(d.getDate() + i);
      dates.push(d);
    }
    return dates;
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    } else {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('es-MX', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const formatWeekDate = (dateStr: string): string => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
  };

  const handleCardClick = async (eventId: number, dateStr: string) => {
    if (selectedEventId === eventId && selectedEventDate === dateStr) {
      setSelectedEventId(null);
      setSelectedEventDate('');
      setAttendanceStudents([]);
      setAttendanceMsg(null);
      return;
    }
    setSelectedEventId(eventId);
    setSelectedEventDate(dateStr);
    setAttendanceMsg(null);
    setAttendanceLoading(true);
    try {
      const students = await getStudentsForSchedule(eventId, dateStr);
      setAttendanceStudents(students);
      const map: Record<number, { status: AttendanceStatus; comments: string }> = {};
      students.forEach(s => {
        map[s.id] = {
          status: (s.attendance?.status as AttendanceStatus) || 'presente',
          comments: s.attendance?.comments || '',
        };
      });
      setAttendanceMap(map);
    } catch (err) {
      console.error(err);
      setAttendanceMsg({ type: 'error', text: 'Error al cargar alumnos' });
    } finally {
      setAttendanceLoading(false);
    }
  };

  const handleMarkAll = (status: AttendanceStatus) => {
    const newMap: Record<number, { status: AttendanceStatus; comments: string }> = {};
    attendanceStudents.forEach(s => {
      const current = attendanceMap[s.id]?.status;
      const locked = !canJustify && (current === 'justificado' || current === 'tardanza');
      newMap[s.id] = { status: locked ? current! : status, comments: attendanceMap[s.id]?.comments || '' };
    });
    setAttendanceMap(newMap);
  };

  const handleSaveAttendance = async () => {
    if (!selectedEventId || !selectedEventDate) return;
    setAttendanceSaving(true);
    setAttendanceMsg(null);
    try {
      await recordAttendanceBatch({
        scheduleId: selectedEventId,
        date: selectedEventDate,
        records: attendanceStudents.map(s => ({
          alumnoId: s.id,
          status: attendanceMap[s.id]?.status || 'presente',
          comments: attendanceMap[s.id]?.comments || undefined,
        })),
      });
      setAttendanceMsg({ type: 'success', text: `Asistencia guardada para ${attendanceStudents.length} alumno${attendanceStudents.length !== 1 ? 's' : ''}` });
      const updated = await getStudentsForSchedule(selectedEventId, selectedEventDate);
      setAttendanceStudents(updated);
    } catch (err: any) {
      setAttendanceMsg({ type: 'error', text: err.message || 'Error al guardar asistencia' });
    } finally {
      setAttendanceSaving(false);
    }
  };

  const statusConfig: Record<AttendanceStatus, { label: string; icon: JSX.Element; classes: string; activeClasses: string }> = {
    presente:   { label: 'Presente',   icon: <CheckCircle className="h-3.5 w-3.5" />,  classes: 'border-gray-300 text-gray-600 hover:bg-green-50 hover:border-green-400 hover:text-green-700',  activeClasses: 'bg-green-100 border-green-500 text-green-800 font-semibold' },
    ausente:    { label: 'Ausente',    icon: <XCircle className="h-3.5 w-3.5" />,      classes: 'border-gray-300 text-gray-600 hover:bg-red-50 hover:border-red-400 hover:text-red-700',     activeClasses: 'bg-red-100 border-red-500 text-red-800 font-semibold' },
    tardanza:   { label: 'Tardanza',   icon: <Clock className="h-3.5 w-3.5" />,        classes: 'border-gray-300 text-gray-600 hover:bg-yellow-50 hover:border-yellow-400 hover:text-yellow-700', activeClasses: 'bg-yellow-100 border-yellow-500 text-yellow-800 font-semibold' },
    justificado:{ label: 'Justificado',icon: <AlertCircle className="h-3.5 w-3.5" />, classes: 'border-gray-300 text-gray-600 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-700',   activeClasses: 'bg-blue-100 border-blue-500 text-blue-800 font-semibold' },
  };

  const renderAttendancePanel = (event: CalendarEvent) => (
    <div className="mt-2 mb-2 border border-indigo-200 rounded-lg bg-white shadow-sm overflow-hidden">
      <div className="bg-indigo-600 px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2 text-white">
          <ClipboardList className="h-4 w-4" />
          <span className="font-semibold text-sm">Lista de Asistencia</span>
          <span className="text-indigo-200 text-xs">— {event.title}</span>
        </div>
        <div className="flex items-center gap-3">
          {canViewGradebook && event.extendedProps.periodId && event.extendedProps.courseId && (
            <button
              onClick={() => navigate(`/app/evaluation/gradebook?periodId=${event.extendedProps.periodId}&courseId=${event.extendedProps.courseId}`)}
              className="flex items-center gap-1 text-xs bg-indigo-500 hover:bg-indigo-400 text-white px-2.5 py-1 rounded-full transition-colors"
            >
              <BookOpen className="h-3.5 w-3.5" />
              Ver calificaciones
            </button>
          )}
          <span className="text-indigo-200 text-xs">{selectedEventDate}</span>
        </div>
      </div>

      {attendanceLoading ? (
        <div className="flex items-center justify-center py-8 gap-2 text-gray-500">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span className="text-sm">Cargando alumnos...</span>
        </div>
      ) : attendanceStudents.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <Users className="h-10 w-10 mx-auto mb-2" />
          <p className="text-sm">No se encontraron alumnos para esta clase</p>
        </div>
      ) : (
        <div className="p-4">
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="text-xs text-gray-500 self-center mr-1">Marcar todos:</span>
            {(Object.keys(statusConfig) as AttendanceStatus[]).filter(s => canJustify || s === 'presente' || s === 'ausente').map(s => (
              <button
                key={s}
                onClick={() => handleMarkAll(s)}
                className={`flex items-center gap-1 text-xs px-3 py-1.5 border rounded-full transition-colors ${statusConfig[s].classes}`}
              >
                {statusConfig[s].icon}
                {statusConfig[s].label}
              </button>
            ))}
          </div>

          <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
            {attendanceStudents.map((student, idx) => {
              const currentStatus = attendanceMap[student.id]?.status || 'presente';
              return (
                <div key={student.id} className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2">
                  <span className="text-xs text-gray-400 w-5 text-right shrink-0">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {student.apellidoPaterno} {student.apellidoMaterno || ''} {student.nombre}
                    </p>
                    <p className="text-xs text-gray-400">{student.nui}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {(Object.keys(statusConfig) as AttendanceStatus[]).filter(s => canJustify || s === 'presente' || s === 'ausente' || s === currentStatus).map(s => (
                      <button
                        key={s}
                        disabled={!canJustify && s !== 'presente' && s !== 'ausente'}
                        title={statusConfig[s].label}
                        onClick={() => setAttendanceMap(prev => ({ ...prev, [student.id]: { ...prev[student.id], status: s } }))}
                        className={`flex items-center gap-1 text-xs px-2 py-1 border rounded transition-colors ${
                          currentStatus === s ? statusConfig[s].activeClasses : 'border-gray-200 text-gray-400 hover:border-gray-400'
                        }`}
                      >
                        {statusConfig[s].icon}
                        <span className="hidden sm:inline">{statusConfig[s].label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {attendanceMsg && (
            <div className={`mt-3 flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${
              attendanceMsg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {attendanceMsg.type === 'success' ? <CheckCircle className="h-4 w-4 shrink-0" /> : <XCircle className="h-4 w-4 shrink-0" />}
              {attendanceMsg.text}
            </div>
          )}

          <div className="mt-4 flex justify-end">
            <Button onClick={handleSaveAttendance} disabled={attendanceSaving} size="sm" className="bg-indigo-600 hover:bg-indigo-700">
              {attendanceSaving ? <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
              {attendanceSaving ? 'Guardando...' : 'Guardar asistencia'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  const renderEventCard = (event: CalendarEvent, dateStr: string) => {
    const isSelected = selectedEventId === event.id && selectedEventDate === dateStr;
    const startTime = event.extendedProps.startTime || event.startTime || event.start?.split('T')[1]?.substring(0, 5) || '';
    const endTime = event.extendedProps.endTime || event.endTime || event.end?.split('T')[1]?.substring(0, 5) || '';
    const sessionLabel = event.extendedProps.sessionNumber && event.extendedProps.totalSessions
      ? `${event.extendedProps.sessionNumber}/${event.extendedProps.totalSessions}`
      : null;

    return (
      <div key={`${event.id}-${dateStr}`} className="mb-2">
        <div
          onClick={() => handleCardClick(event.id, dateStr)}
          className={`border-l-4 p-3 rounded-r-lg cursor-pointer transition-colors ${
            isSelected
              ? 'bg-indigo-50 border-indigo-500 hover:bg-indigo-100'
              : 'bg-blue-50 border-blue-500 hover:bg-blue-100'
          }`}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h4 className={`font-semibold text-sm md:text-base ${isSelected ? 'text-indigo-900' : 'text-blue-900'}`}>
                {event.title}
                {sessionLabel && (
                  <span className={`ml-2 text-xs px-2 py-0.5 rounded-full font-normal ${isSelected ? 'bg-indigo-200 text-indigo-800' : 'bg-blue-200 text-blue-800'}`}>
                    Clase {sessionLabel}
                  </span>
                )}
              </h4>
              <div className="mt-1 space-y-1 text-xs md:text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3 md:h-4 md:w-4" />
                  <span>{startTime} - {endTime}</span>
                </div>
                <div className="flex items-center gap-1">
                  <MapPin className="h-3 w-3 md:h-4 md:w-4" />
                  <span>{event.extendedProps.classroom}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Users className="h-3 w-3 md:h-4 md:w-4" />
                  <span>{event.extendedProps.program}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 ml-2 shrink-0">
              <span className={`text-xs hidden sm:flex items-center gap-1 ${isSelected ? 'text-indigo-600' : 'text-gray-400'}`}>
                <ClipboardList className="h-3.5 w-3.5" />
                Asistencia
              </span>
              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isSelected ? 'rotate-180 text-indigo-600' : 'text-gray-400'}`} />
            </div>
          </div>
        </div>
        {isSelected && renderAttendancePanel(event)}
      </div>
    );
  };

  if (loading && !professorsLoaded) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto text-blue-500" />
          <p className="mt-2 text-gray-500">Cargando agenda...</p>
        </div>
      </div>
    );
  }

  if (professorsLoaded && professors.length === 0 && !myTeacherId) {
    return (
      <div className="p-4 max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Calendar className="h-6 w-6 text-blue-500" />
            Agenda Digital Docente
          </h1>
        </div>
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-8">
            <div className="text-center">
              <Users className="h-14 w-14 mx-auto text-yellow-400 mb-4" />
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">
                Perfil de docente no vinculado
              </h3>
              <p className="text-yellow-700 max-w-md mx-auto">
                Tu cuenta de usuario no está vinculada a un perfil de docente en el sistema.
                Contacta al administrador para que cree o vincule tu perfil de docente.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const weekDates = getWeekDates(currentDate);
  const todayEvents = getEventsForDate(currentDate);

  return (
    <div className="p-4 max-w-5xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Calendar className="h-6 w-6 text-blue-500" />
              Agenda Digital Docente
            </h1>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => selectedProfessorId && fetchAgendaForProfessor(selectedProfessorId)} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-1" />
              Actualizar
            </Button>
          </div>
        </div>

        {canViewAll && professors.length > 1 ? (
          <div className="bg-white border rounded-lg p-4 mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Profesor
            </label>
            <select
              value={selectedProfessorId || ''}
              onChange={(e) => {
                const newId = parseInt(e.target.value);
                if (!isNaN(newId)) {
                  setSelectedProfessorId(newId);
                }
              }}
              className="w-full md:w-96 px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            >
              <option value="">Seleccionar profesor...</option>
              {professors.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name} {p.id === myTeacherId ? '(Yo)' : ''}
                </option>
              ))}
            </select>
            {teacher && selectedProfessorId && (
              <p className="text-xs text-gray-500 mt-1">
                Mostrando agenda de: <span className="font-medium">{teacher.name}</span>
              </p>
            )}
          </div>
        ) : teacher ? (
          <div className="bg-white border rounded-lg p-4 mb-4">
            <p className="text-sm text-gray-700">
              Mi agenda: <span className="font-medium">{teacher.name}</span>
            </p>
          </div>
        ) : null}
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50 mb-6">
          <CardContent className="p-6">
            <div className="text-center">
              <Calendar className="h-12 w-12 mx-auto text-red-400 mb-4" />
              <h3 className="text-lg font-semibold text-red-800 mb-2">Error</h3>
              <p className="text-red-600">{error}</p>
              <Button onClick={() => selectedProfessorId && fetchAgendaForProfessor(selectedProfessorId)} variant="outline" className="mt-4">
                Reintentar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!error && selectedProfessorId && (
        <>
          {todayClasses.length > 0 && viewMode !== 'report' && (
            <Card className="mb-6 border-green-200 bg-green-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-green-800">
                  <BookOpen className="h-5 w-5" />
                  Clases de Hoy
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {todayClasses.map((cls) => (
                    <div
                      key={cls.id}
                      className="bg-white p-3 rounded-lg border border-green-200 flex items-center justify-between"
                    >
                      <div>
                        <p className="font-medium text-gray-900">
                          {cls.course}
                          {cls.sessionNumber && cls.totalSessions && (
                            <span className="ml-2 text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full">
                              {cls.sessionNumber}/{cls.totalSessions}
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-gray-500">{cls.program}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-green-700">{cls.time}</p>
                        <p className="text-sm text-gray-500">{cls.classroom}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-2">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'day' | 'week' | 'report')}>
                  <TabsList>
                    <TabsTrigger value="day">Vista Dia</TabsTrigger>
                    <TabsTrigger value="week">Vista Semana</TabsTrigger>
                    <TabsTrigger value="report">
                      <FileText className="h-4 w-4 mr-1" />
                      Reporte
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                {viewMode !== 'report' && (
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => navigateDate('prev')}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={goToToday}>
                      Hoy
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => navigateDate('next')}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>

            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
                  <span className="ml-2 text-gray-500">Cargando...</span>
                </div>
              ) : viewMode === 'day' ? (
                <div>
                  <h3 className="text-lg font-semibold mb-4 capitalize">
                    {formatDate(currentDate)}
                    {isToday(currentDate) && (
                      <span className="ml-2 text-xs bg-blue-500 text-white px-2 py-1 rounded-full">
                        Hoy
                      </span>
                    )}
                  </h3>

                  {isToday(currentDate) && pendingEvents.length > 0 && (
                    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3">
                      <p className="text-sm font-semibold text-amber-900 mb-1">
                        Clases atrasadas sin asistencia ({pendingEvents.length})
                      </p>
                      <p className="text-xs text-amber-800 mb-2">
                        Si tu grupo va una clase atrasada, toma aquí la asistencia de la clase que realmente impartiste hoy.
                        Se guardará con la fecha de hoy.
                      </p>
                      {pendingEvents.map(event => (
                        <div key={`pending-${event.id}`}>
                          <p className="text-xs text-amber-700 ml-1 mt-1">
                            Programada para el {event.start ? formatWeekDate(event.start.split('T')[0]) : '—'}
                          </p>
                          {renderEventCard(event, currentDate.toISOString().split('T')[0])}
                        </div>
                      ))}
                    </div>
                  )}

                  {todayEvents.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Calendar className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                      <p>No hay clases programadas para este dia</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {todayEvents
                        .sort((a, b) => {
                          const timeA = a.extendedProps.startTime || a.startTime || a.start?.split('T')[1] || '';
                          const timeB = b.extendedProps.startTime || b.startTime || b.start?.split('T')[1] || '';
                          return timeA.localeCompare(timeB);
                        })
                        .map(event => renderEventCard(event, currentDate.toISOString().split('T')[0]))}
                    </div>
                  )}
                </div>
              ) : viewMode === 'week' ? (
                <div className="grid grid-cols-7 gap-1 md:gap-2">
                  {weekDates.map((date, idx) => {
                    const dayEvents = getEventsForDate(date);
                    const isTodayDate = isToday(date);

                    return (
                      <div
                        key={idx}
                        className={`min-h-[150px] md:min-h-[200px] border rounded-lg p-1 md:p-2 ${
                          isTodayDate ? 'bg-blue-50 border-blue-300' : 'bg-gray-50'
                        }`}
                      >
                        <div className={`text-center mb-2 ${isTodayDate ? 'text-blue-600' : 'text-gray-600'}`}>
                          <div className="text-xs font-medium">{dayNamesShort[idx]}</div>
                          <div className={`text-lg font-bold ${
                            isTodayDate ? 'bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto' : ''
                          }`}>
                            {date.getDate()}
                          </div>
                        </div>

                        <div className="space-y-1 overflow-y-auto max-h-[120px] md:max-h-[150px]">
                          {dayEvents.length === 0 ? (
                            <p className="text-xs text-gray-400 text-center">-</p>
                          ) : (
                            dayEvents
                              .sort((a, b) => {
                                const timeA = a.extendedProps.startTime || a.startTime || a.start?.split('T')[1] || '';
                                const timeB = b.extendedProps.startTime || b.startTime || b.start?.split('T')[1] || '';
                                return timeA.localeCompare(timeB);
                              })
                              .map(event => {
                                const sessionLabel = event.extendedProps.sessionNumber && event.extendedProps.totalSessions
                                  ? `${event.extendedProps.sessionNumber}/${event.extendedProps.totalSessions}`
                                  : null;
                                return (
                                  <div
                                    key={event.id}
                                    onClick={() => { setCurrentDate(date); setViewMode('day'); }}
                                    title="Clic para ver asistencia"
                                    className="bg-blue-100 border-l-2 border-blue-500 p-1 rounded text-xs cursor-pointer hover:bg-blue-200 transition-colors"
                                  >
                                    <div className="font-medium text-blue-900 truncate">
                                      {event.extendedProps.courseCode || event.title.split(' - ')[0]}
                                      {sessionLabel && (
                                        <span className="ml-1 text-blue-600">({sessionLabel})</span>
                                      )}
                                    </div>
                                    <div className="text-blue-700">
                                      {event.extendedProps.startTime || event.startTime || event.start?.split('T')[1]?.substring(0, 5)}
                                    </div>
                                    <div className="text-gray-600 truncate">
                                      {event.extendedProps.classroom}
                                    </div>
                                  </div>
                                );
                              })
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div>
                  <div className="flex flex-col md:flex-row gap-3 mb-6 items-end">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Inicio</label>
                      <input
                        type="date"
                        value={reportStartDate}
                        onChange={(e) => setReportStartDate(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Fin</label>
                      <input
                        type="date"
                        value={reportEndDate}
                        onChange={(e) => setReportEndDate(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <Button onClick={fetchWeeklyReport} size="sm" disabled={reportLoading}>
                      <Search className="h-4 w-4 mr-1" />
                      {reportLoading ? 'Buscando...' : 'Buscar'}
                    </Button>
                    {weekSummaries.length > 0 && (
                      <>
                        <Button onClick={printReport} size="sm" variant="outline">
                          <Printer className="h-4 w-4 mr-1" />
                          Imprimir
                        </Button>
                        <Button onClick={downloadCsv} size="sm" variant="outline">
                          <Download className="h-4 w-4 mr-1" />
                          Descargar CSV
                        </Button>
                      </>
                    )}
                  </div>

                  {reportLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
                      <span className="ml-2 text-gray-500">Generando reporte...</span>
                    </div>
                  ) : weekSummaries.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                      <p>No se encontraron clases en el rango seleccionado</p>
                      <p className="text-xs mt-1">Selecciona un rango de fechas y presiona Buscar</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {weekSummaries.map((week, wIdx) => (
                        <div key={wIdx} className="border rounded-lg overflow-hidden">
                          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3 text-white">
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold">
                                Semana: {formatWeekDate(week.weekStart)} - {formatWeekDate(week.weekEnd)}
                              </h4>
                              <div className="flex gap-4 text-sm">
                                <span>{week.subjectCount} materia{week.subjectCount !== 1 ? 's' : ''}</span>
                                <span>{week.classCount} clase{week.classCount !== 1 ? 's' : ''}</span>
                              </div>
                            </div>
                          </div>

                          <div className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                              {week.subjects.map((subj, sIdx) => (
                                <div key={sIdx} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                  <h5 className="font-semibold text-blue-900 text-sm">
                                    {subj.courseCode} - {subj.courseName}
                                  </h5>
                                  <div className="mt-1 text-xs text-gray-600">
                                    <div className="flex items-center gap-1">
                                      <Users className="h-3 w-3" />
                                      <span>{subj.groups.join(', ')}</span>
                                    </div>
                                    {subj.sessions.length > 0 && (
                                      <div className="flex items-center gap-1 mt-1">
                                        <BookOpen className="h-3 w-3" />
                                        <span>Clases: {subj.sessions.join(', ')}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>

                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-gray-100 text-gray-700">
                                    <th className="px-3 py-2 text-left">Fecha</th>
                                    <th className="px-3 py-2 text-left">Dia</th>
                                    <th className="px-3 py-2 text-left">Horario</th>
                                    <th className="px-3 py-2 text-left">Materia</th>
                                    <th className="px-3 py-2 text-left">Grupo</th>
                                    <th className="px-3 py-2 text-left">Aula</th>
                                    <th className="px-3 py-2 text-left">Clase</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {week.rows.map((row, rIdx) => (
                                    <tr key={rIdx} className="border-b hover:bg-gray-50">
                                      <td className="px-3 py-2 whitespace-nowrap">
                                        {row.date ? new Date(row.date + 'T00:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'short' }) : '-'}
                                      </td>
                                      <td className="px-3 py-2 capitalize">{row.day || '-'}</td>
                                      <td className="px-3 py-2 whitespace-nowrap">{row.startTime} - {row.endTime}</td>
                                      <td className="px-3 py-2">
                                        <span className="font-medium">{row.courseCode}</span>
                                        <span className="text-gray-500 ml-1 text-xs">{row.courseName}</span>
                                      </td>
                                      <td className="px-3 py-2 text-xs">{row.program}</td>
                                      <td className="px-3 py-2">{row.classroom}</td>
                                      <td className="px-3 py-2">
                                        {row.sessionLabel ? (
                                          <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs font-medium">
                                            {row.sessionLabel}
                                          </span>
                                        ) : '-'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

    </div>
  );
}
